/**
 * 月次【スタッフ別】(現行シート「月次【ｽﾀｯﾌ別】」の再現。2026-09-24 発注者指示。P-64)
 *  - 上段 = 種別(出勤/時給・在宅/時給・在宅/歩合・管理者なし合計・管理者あり合計・不用品(管抜)・不用品・総合計)ごとに
 *    累計(原価率・取得成績・アポ・時間・報酬・現状売上)と、日別(または週別)の アポ・時間・報酬・原価率
 *  - 下段 = スタッフ(BB ID)ごとに 形態・架電内容・単価・交通費(往復)・出勤日数・勤務時間・アポ・取得成績・RANK・報酬・交通費・原価率 と、
 *    同じ日別(または週別)の列
 *  月だけの画面(期間は月の前後で移動)。横に広いので**枠の中だけ横スクロール**する(.report-scroll)
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import MultiSelect from '@core/components/MultiSelect';
import { SearchBox, matchKeyword, sortRows, useSort } from '@core/components/tableTools';
import { downloadCsv } from '@core/masters/csvImport';
import Panel from '@core/components/ui/Panel';
import RankBadge from '@core/components/ui/RankBadge';
import SegmentedControl from '@core/components/ui/SegmentedControl';
import SortableTh from '@core/components/ui/SortableTh';
import TableDensity, { useTableDensity } from '@core/components/ui/TableDensity';
import ToneValue from '@core/components/ui/ToneValue';
import { weekdayLabel } from '@core/lib/date';
import { fetchOperators } from '@core/masters/operatorRepo';
import { dayTextClass } from '@core/lib/holidays';
import { BUSINESS_LABELS, commuteOn, styleLabel, type BusinessType, type DateStr, type Operator, type PayType, type WorkStyle, type Yen } from '@core/lib/types';
import { formatCount, formatCurrency, formatDecimal, formatPercent } from '@core/utils/formatters';
import {
  CATEGORY_KEYS,
  CATEGORY_ROW_LABELS,
  addAgg,
  apphOf,
  categoryRows,
  costRate,
  emptyAgg,
  rankOf,
  targetFor,
  weekOfMonth,
  type Aggregate,
  type CategoryKey,
  type Rank,
  type StaffMetrics,
} from './calc';
import { HELP, costRateTone } from './colors';
import PeriodToolbar from './PeriodToolbar';
import { fetchMonthClose } from './monthCloseRepo';
import { usePeriodData } from './usePeriod';

/** 推移の列: 日別 / 週別(W1〜W6) / 出さない */
type Trend = 'day' | 'week' | 'none';

type SortKey = 'name' | 'style' | 'business' | 'wage' | 'days' | 'hours' | 'appointments' | 'apph' | 'pay' | 'commute' | 'commute_base' | 'rate';

/** 1人(BB ID)の月の集計 */
type StaffRow = {
  bb_id: string;
  op_id: string;
  name: string;
  work_style: WorkStyle;
  pay_type: PayType;
  business: BusinessType;
  /** その月の最後に使われた単価(時給 or 件数単価) */
  wage: Yen;
  /** マスタの往復交通費(減額前) */
  commute_base: Yen;
  /** 稼働のあった日数 */
  days: number;
  agg: Aggregate;
  byCol: Map<string, Aggregate>;
};

const W_LABEL = 156;
const W_NO = 44;
const stickyLabel = { left: 0, width: W_LABEL, minWidth: W_LABEL, maxWidth: W_LABEL } as const;
/** 下段の No 列(左端)。スタッフ名はその右 */
const stickyNo = { left: 0, width: W_NO, minWidth: W_NO, maxWidth: W_NO } as const;
const stickyName = { left: W_NO, width: W_LABEL, minWidth: W_LABEL, maxWidth: W_LABEL } as const;

export default function MonthlyStaffPage() {
  const period = usePeriodData('month');
  const { rows, targets, loading, range } = period;
  const [trend, setTrend] = useState<Trend>('day');
  const [density, setDensity] = useTableDensity('cc.monthlyStaff.density2');
  const [keyword, setKeyword] = useState('');
  const [hideZero, setHideZero] = useState(true);
  /** 種別ごと・スタッフごとの表示/非表示(2026-09-28 発注者指示)。＋／− で開閉 */
  const [showCats, setShowCats] = useState(true);
  const [showStaff, setShowStaff] = useState(true);
  /** 形態・架電内容の絞り込み(2026-09-28 発注者指示) */
  const [styleF, setStyleF] = useState<Set<string>>(new Set());
  const [bizF, setBizF] = useState<Set<BusinessType>>(new Set());
  const { sort, toggle: toggleSort } = useSort<SortKey>(null);
  const [operators, setOperators] = useState<Operator[]>([]);
  /** 前渡(月締め①の「前払い」)。BB ID → 円 */
  const [advances, setAdvances] = useState<Record<string, number>>({});
  useEffect(() => {
    // 交通費(往復)はオペレーターマスタの金額(現行シートのI列)。読めなくても他の数字は出す
    void fetchOperators()
      .then(setOperators)
      .catch(() => undefined);
  }, []);
  const commuteMaster = useMemo(() => new Map(operators.map((o) => [o.id, commuteOn(o, range.to)])), [operators, range.to]);
  useEffect(() => {
    void fetchMonthClose(range.anchor.slice(0, 7))
      .then((v) => setAdvances(Object.fromEntries(Object.entries(v.pay).map(([bb, x]) => [bb, Number(x.advance ?? 0)]))))
      .catch(() => setAdvances({}));
  }, [range.anchor]);

  /** 原価率の赤: 給湯器=cost_rate_total(既定45%) / 不用品=cost_rate_fuyohin(既定50%)。月次設定で変えられる */
  const costTarget = targets?.cost_rate_total ?? null;
  const fuyohinTarget = targets?.cost_rate_fuyohin ?? null;
  const targetOf = (business: BusinessType) => (business === 'fuyohin' ? fuyohinTarget : costTarget);

  /** 推移の列(日別=1日〜末日 / 週別=W1〜W6) */
  const columns = useMemo(() => {
    if (trend === 'none') return [] as { key: string; label: string; sub: string; cls: string }[];
    if (trend === 'day') return range.dates.map((d) => ({ key: d, label: String(Number(d.slice(8))), sub: weekdayLabel(d).replace(/[()]/g, ''), cls: dayTextClass(d) }));
    const weeks = Array.from(new Set(range.dates.map((d) => weekOfMonth(d))));
    return weeks.map((w) => ({ key: `W${w}`, label: `W${w}`, sub: '', cls: '' }));
  }, [trend, range.dates]);

  const colKeyOf = (date: DateStr) => (trend === 'week' ? `W${weekOfMonth(date)}` : date);

  // ---- 上段: 種別ごと ----
  const catTotals = useMemo(() => categoryRows(rows), [rows]);
  const catByCol = useMemo(() => {
    const buckets = new Map<string, StaffMetrics[]>();
    if (trend !== 'none') {
      for (const r of rows) {
        const k = colKeyOf(r.date);
        const list = buckets.get(k);
        if (list) list.push(r);
        else buckets.set(k, [r]);
      }
    }
    const m = new Map<string, Record<CategoryKey, Aggregate>>();
    for (const [k, list] of buckets) m.set(k, categoryRows(list));
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, trend]);

  // ---- 下段: スタッフ(BB ID)ごと ----
  const staffRows = useMemo(() => {
    const m = new Map<string, StaffRow & { wageDate: DateStr; dates: Set<DateStr> }>();
    for (const r of rows) {
      let e = m.get(r.bb_id);
      if (!e) {
        e = {
          bb_id: r.bb_id,
          op_id: r.op_id,
          name: r.name,
          work_style: r.work_style,
          pay_type: r.pay_type,
          business: r.business,
          wage: r.wage,
          wageDate: r.date,
          commute_base: r.commute_base ?? 0,
          days: 0,
          dates: new Set<DateStr>(),
          agg: emptyAgg(),
          byCol: new Map<string, Aggregate>(),
        };
        m.set(r.bb_id, e);
      }
      e.agg = addAgg(e.agg, r);
      // 単価はその月の最後に使われた値(月の途中で改定されたときは新しい方)
      if (r.date >= e.wageDate && r.wage > 0) {
        e.wage = r.wage;
        e.wageDate = r.date;
      }
      if ((r.commute_base ?? 0) > e.commute_base) e.commute_base = r.commute_base ?? 0;
      if (r.appointments > 0 || r.worked_hours > 0) e.dates.add(r.date);
      if (trend !== 'none') {
        const k = colKeyOf(r.date);
        e.byCol.set(k, addAgg(e.byCol.get(k) ?? emptyAgg(), r));
      }
    }
    return [...m.values()].map((e) => ({ ...e, days: e.dates.size }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, trend]);

  /** 形態の選択肢(その月に出てくるものだけ) */
  const styleOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of staffRows) m.set(`${r.work_style}/${r.pay_type}`, styleLabel(r.work_style, r.pay_type));
    return [...m.entries()].map(([key, label]) => ({ key, label }));
  }, [staffRows]);

  /** 交通費(往復)= マスタの金額。マスタに無ければ日次の行に残っている減額前の金額 */
  const commuteBaseOf = (s: StaffRow) => commuteMaster.get(s.op_id) ?? s.commute_base;

  const visible = useMemo(() => {
    const list = staffRows.filter(
      (s) =>
        matchKeyword(keyword, s.name, s.bb_id) &&
        (!hideZero || s.agg.appointments > 0 || s.agg.hours > 0) &&
        (styleF.size === 0 || styleF.has(`${s.work_style}/${s.pay_type}`)) &&
        (bizF.size === 0 || bizF.has(s.business)),
    );
    // 既定の並び: 形態(出勤→在宅時給→在宅歩合→タイミー→管理)→名前。見出しを押すと並べ替え
    const order: WorkStyle[] = ['shinagawa', 'zaitaku', 'timee', 'kanri'];
    const base = [...list].sort((a, b) => order.indexOf(a.work_style) - order.indexOf(b.work_style) || a.pay_type.localeCompare(b.pay_type) || a.name.localeCompare(b.name, 'ja'));
    return sortRows(base, sort, (s, key) => {
      switch (key) {
        case 'name':
          return s.name;
        case 'style':
          return styleLabel(s.work_style, s.pay_type);
        case 'business':
          return BUSINESS_LABELS[s.business];
        case 'wage':
          return s.wage;
        case 'days':
          return s.days;
        case 'hours':
          return s.agg.hours;
        case 'appointments':
          return s.agg.appointments;
        case 'apph':
          return apphOf(s.agg) ?? -1;
        case 'pay':
          return s.agg.pay;
        case 'commute':
          return s.agg.commute;
        case 'commute_base':
          return commuteBaseOf(s);
        case 'rate':
          return costRate(s.agg) ?? -1;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffRows, keyword, hideZero, sort, commuteMaster, styleF, bizF]);

  const visibleTotal = useMemo(() => visible.reduce((agg, s) => ({ ...agg, appointments: agg.appointments + s.agg.appointments, hours: agg.hours + s.agg.hours, pay: agg.pay + s.agg.pay, revenue: agg.revenue + s.agg.revenue, commute: agg.commute + s.agg.commute }), emptyAgg()), [visible]);

  /** 現行シートの色: 取得成績 2.5以上=赤 / 2.0〜2.49=橙(どちらも白文字) */
  const apphClass = (v: number | null) => (v === null || !Number.isFinite(v) ? '' : v >= 2.5 ? ' sheet-apph-hi' : v >= 2 ? ' sheet-apph-mid' : '');
  /** 現行シートの色: 原価率が目標より低い=青(白文字)。目標超過は赤字(ToneValue) */
  const costClass = (v: number | null, target: number | null) => (v === null || target === null ? '' : v < target ? ' sheet-cost-good' : '');

  /** Excel(CSV)で出す。列は画面と同じ並び */
  const downloadExcel = () => {
    const head = ['No', 'スタッフ名', 'BB ID', '形態', '架電内容', '単価', '出勤日数', '勤務時間', 'アポ', '取得成績', 'RANK', '報酬', '交通費', '原価率'];
    const body = visible.map((s, i) => {
      const apph = apphOf(s.agg);
      return [
        i + 1,
        `"${s.name}"`,
        s.bb_id,
        styleLabel(s.work_style, s.pay_type),
        BUSINESS_LABELS[s.business],
        s.wage,
        s.days,
        s.agg.hours,
        s.agg.appointments,
        apph === null ? '' : apph.toFixed(2),
        rankOf(apph, s.business),
        s.agg.pay,
        s.agg.commute,
        costRate(s.agg) === null ? '' : costRate(s.agg)!.toFixed(1),
      ].join(',');
    });
    downloadCsv(`月次スタッフ別_${range.anchor.slice(0, 7)}.csv`, `${head.join(',')}\n${body.join('\n')}\n`);
  };

  /** 日別・週別の4列(アポ・時間・報酬・原価率) */
  const trendCells = (agg: Aggregate | undefined, target: number | null, keyPrefix: string) => {
    const rate = agg ? costRate(agg) : null;
    return (
      <Fragment key={keyPrefix}>
        <td className="is-num col-group sheet-col-appt">
          {agg && agg.appointments > 0 ? formatCount(agg.appointments) : <span className="cell-na">—</span>}
        </td>
        <td className="is-num sheet-col-hours">
          {agg && agg.hours > 0 ? formatDecimal(agg.hours, { digits: 1 }) : <span className="cell-na">—</span>}
        </td>
        <td className="is-num sheet-col-pay">
          {agg && agg.pay > 0 ? formatCurrency(agg.pay) : <span className="cell-na">—</span>}
        </td>
        <td className="is-num sheet-col-rate">
          {rate === null ? <span className="cell-na">—</span> : <ToneValue tone={costRateTone(rate, target)}>{formatPercent(rate)}</ToneValue>}
        </td>
      </Fragment>
    );
  };

  /** 推移の見出し(2段: 上=日付/週、下=アポ・時間・報酬・原価率) */
  const trendHead = (
    <>
      {columns.map((c) => (
        <th key={c.key} colSpan={4} scope="colgroup" className={`is-center col-group ${c.cls}`}>
          {c.label}
          {c.sub && <span className="cell-sub">{c.sub}</span>}
        </th>
      ))}
    </>
  );
  const trendSubHead = (
    <>
      {columns.map((c) => (
        <Fragment key={c.key}>
          <th scope="col" className="is-num col-group sheet-col-appt">
            アポ
          </th>
          <th scope="col" className="is-num sheet-col-hours">
            時間
          </th>
          <th scope="col" className="is-num sheet-col-pay">
            報酬
          </th>
          <th scope="col" className="is-num sheet-col-rate">
            原価率
          </th>
        </Fragment>
      ))}
    </>
  );

  return (
    <div className="screen master-screen full monthly-staff">
      <PeriodToolbar
        period={period}
        showModes={false}
        right={
          <>
            <SegmentedControl
              label="推移"
              showLabel
              size="sm"
              options={[
                { key: 'day', label: '日別' },
                { key: 'week', label: '週別' },
                { key: 'none', label: '出さない' },
              ]}
              value={trend}
              onChange={(v) => setTrend(v as Trend)}
            />
            <TableDensity value={density} onChange={setDensity} />
            <button type="button" className="ui-btn ui-btn-primary ui-btn-sm" onClick={downloadExcel}>
              Excel出力
            </button>
            <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => window.print()}>
              印刷
            </button>
          </>
        }
      />
      {loading || !targets ? (
        <div className="ui-panel">
          <div className="ui-empty">読み込み中…</div>
        </div>
      ) : rows.length === 0 ? (
        <div className="ui-panel">
          <div className="ui-empty">{range.label} の集計データはまだありません。締め作業でOPレポート・対応履歴のCSVを取り込むと、ここに出ます</div>
        </div>
      ) : (
        <>
          <Panel
            title="種別ごと"
            meta={range.label}
            actions={
              <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" aria-expanded={showCats} onClick={() => setShowCats((v) => !v)}>
                {showCats ? '−' : '＋'}
              </button>
            }
            note={`**現状売上 = 取得 × 会社単価**(売上見込)、**原価率 = 報酬 ÷ 現状売上**です。赤は目標(給湯器 ${formatPercent(costTarget, { digits: 1 })} ／ 不用品 ${formatPercent(fuyohinTarget, { digits: 1 })}。月次設定で変えられます)を超えた原価率。取得成績は1時間あたりの取得数`}
          >
            {showCats && (
            <div className="report-scroll is-auto">
              <table className={`report-table is-wide is-sheet${density === 'compact' ? ' is-compact' : ''}`}>
                <caption className="sr-only">
                  {range.label} の種別ごとの集計
                </caption>
                <thead>
                  <tr>
                    <th scope="col" rowSpan={2} className="is-sticky is-sticky-edge" style={stickyLabel}>
                      種別
                    </th>
                    <th scope="col" rowSpan={2} className="is-center">
                      原価率
                    </th>
                    <th scope="col" rowSpan={2} className="is-center">
                      取得成績
                    </th>
                    <th scope="col" rowSpan={2} className="is-center">
                      アポ
                    </th>
                    <th scope="col" rowSpan={2} className="is-center">
                      時間
                    </th>
                    <th scope="col" rowSpan={2} className="is-center">
                      報酬
                    </th>
                    <th scope="col" rowSpan={2} className="is-center">
                      現状売上
                    </th>
                    {trendHead}
                    {/* 最右の合計(2026-09-28 発注者指示) */}
                    <th colSpan={4} scope="colgroup" className="is-center col-group sheet-total-head">
                      合計
                    </th>
                  </tr>
                  <tr>
                    {trendSubHead}
                    <th scope="col" className="is-num col-group sheet-col-appt">
                      アポ
                    </th>
                    <th scope="col" className="is-num sheet-col-hours">
                      時間
                    </th>
                    <th scope="col" className="is-num sheet-col-pay">
                      報酬
                    </th>
                    <th scope="col" className="is-num sheet-col-rate">
                      原価率
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {CATEGORY_KEYS.map((key) => {
                    const agg = catTotals[key];
                    const target = targetFor(key, targets);
                    const rate = costRate(agg);
                    const isTotal = key === 'no_manager' || key === 'with_manager' || key === 'total';
                    return (
                      <tr key={key} className={`${isTotal ? 'is-subtotal' : ''}${key === 'total' ? ' sheet-grand' : ''}`.trim() || undefined}>
                        <th scope="row" className="is-sticky is-sticky-edge" style={stickyLabel}>
                          {CATEGORY_ROW_LABELS[key]}
                        </th>
                        <td className={`is-center${costClass(rate, target)}`}>
                          {rate === null ? <span className="cell-na">—</span> : <ToneValue tone={costRateTone(rate, target)}>{formatPercent(rate)}</ToneValue>}
                        </td>
                        <td className={`is-center${apphClass(apphOf(agg))}`}>{formatDecimal(apphOf(agg))}</td>
                        <td className="is-center">{formatCount(agg.appointments)}</td>
                        <td className="is-center">{formatDecimal(agg.hours, { digits: 1 })}</td>
                        <td className="is-center">{formatCurrency(agg.pay)}</td>
                        <td className="is-center">{formatCurrency(agg.revenue)}</td>
                        {columns.map((c) => trendCells(catByCol.get(c.key)?.[key], target, `${key}-${c.key}`))}
                        {/* 最右の合計 */}
                        <td className="is-num col-group sheet-col-appt">{formatCount(agg.appointments)}</td>
                        <td className="is-num sheet-col-hours">{formatDecimal(agg.hours, { digits: 1 })}</td>
                        <td className="is-num sheet-col-pay">{formatCurrency(agg.pay)}</td>
                        <td className="is-num sheet-col-rate">
                          {rate === null ? <span className="cell-na">—</span> : <ToneValue tone={costRateTone(rate, target)}>{formatPercent(rate)}</ToneValue>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}
          </Panel>

          <Panel
            title="スタッフごと"
            meta={`${formatCount(visible.length, { unit: '名' })} ／ ${range.label}`}
            note="1行 = **BB ID**(同じ人でも給湯器と不用品は別の行)。**単価**はその月の最後に使われた時給・件数単価、**交通費(往復)**はマスタの金額(減額前)、右の**交通費**は実際に支給した合計です"
            actions={
              <>
                <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" aria-expanded={showStaff} onClick={() => setShowStaff((v) => !v)}>
                  {showStaff ? '−' : '＋'}
                </button>
                <SearchBox className="ui-search is-sm" value={keyword} onChange={setKeyword} placeholder="名前・BB IDで検索" />
                <MultiSelect label="全ての形態" options={styleOptions} value={styleF} onChange={setStyleF} />
                <MultiSelect
                  label="全ての架電内容"
                  options={(Object.keys(BUSINESS_LABELS) as BusinessType[]).map((k) => ({ key: k, label: BUSINESS_LABELS[k] }))}
                  value={bizF}
                  onChange={setBizF}
                />
                <SegmentedControl
                  label="稼働なし"
                  size="sm"
                  options={[
                    { key: 'hide', label: '隠す' },
                    { key: 'show', label: '出す' },
                  ]}
                  value={hideZero ? 'hide' : 'show'}
                  onChange={(v) => setHideZero(v === 'hide')}
                />
              </>
            }
          >
            {showStaff && (
            <div className="report-scroll is-tall">
              <table className={`report-table is-wide is-sheet${density === 'compact' ? ' is-compact' : ''}`}>
                <caption className="sr-only">{range.label} のスタッフごとの集計</caption>
                <thead>
                  <tr>
                    <th scope="col" rowSpan={2} className="is-num is-sticky" style={stickyNo}>
                      No
                    </th>
                    <SortableTh label="スタッフ名(BB ID)" sortKey="name" sort={sort} onSort={toggleSort} defaultDir="asc" rowSpan={2} className="is-sticky is-sticky-edge" style={stickyName} />
                    <SortableTh label="出勤形態" sortKey="style" sort={sort} onSort={toggleSort} defaultDir="asc" rowSpan={2} />
                    <SortableTh label="アポ/h" sortKey="apph" sort={sort} onSort={toggleSort} numeric rowSpan={2} className="is-center" help={{ ...HELP.apph, title: 'アポ/h(1時間あたりの取得数)' }} />
                    <SortableTh label="出勤日数" sortKey="days" sort={sort} onSort={toggleSort} numeric rowSpan={2} />
                    <SortableTh label="勤務時間" sortKey="hours" sort={sort} onSort={toggleSort} numeric rowSpan={2} />
                    <SortableTh label="RANK" rowSpan={2} className="is-center" help={HELP.rank} />
                    <SortableTh label="報酬" sortKey="wage" sort={sort} onSort={toggleSort} numeric rowSpan={2} help={{ title: '報酬(単価)', what: '時給、または歩合の1件あたりの単価。月末時点の値', better: '—' }} />
                    <SortableTh label="交通費(往復)" sortKey="commute_base" sort={sort} onSort={toggleSort} numeric rowSpan={2} />
                    {trendHead}
                    {/* 最右の合計(現行シートと同じ並び。2026-09-28 発注者指示) */}
                    <th colSpan={5} scope="colgroup" className="is-center col-group sheet-total-head">
                      合計
                    </th>
                  </tr>
                  <tr>
                    {trendSubHead}
                    <th scope="col" className="is-num col-group sheet-col-appt">
                      アポ
                    </th>
                    <th scope="col" className="is-num sheet-col-hours">
                      時間
                    </th>
                    <th scope="col" className="is-num sheet-col-pay">
                      報酬
                    </th>
                    <th scope="col" className="is-num">
                      交通費
                    </th>
                    <th scope="col" className="is-num">
                      前渡
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((s, i) => {
                    const apph = apphOf(s.agg);
                    const rank: Rank = rankOf(apph, s.business);
                    const target = targetOf(s.business);
                    return (
                      <tr key={s.bb_id}>
                        <td className="is-num is-sticky" style={stickyNo}>
                          {i + 1}
                        </td>
                        <th scope="row" className="is-sticky is-sticky-edge" style={stickyName}>
                          {s.name}
                          <span className="cell-sub">{s.bb_id}</span>
                        </th>
                        <td>{styleLabel(s.work_style, s.pay_type)}</td>
                        <td className="is-num is-center">
                          {apph === null ? <span className="cell-na">—</span> : <span className={`apph-chip${apphClass(apph)}`}>{formatDecimal(apph)}</span>}
                        </td>
                        <td className="is-num">{s.days > 0 ? formatCount(s.days) : <span className="cell-na">—</span>}</td>
                        <td className="is-num">{formatDecimal(s.agg.hours, { digits: 1 })}</td>
                        <td className="is-center">
                          <RankBadge rank={rank} />
                        </td>
                        <td className="is-num">{s.wage > 0 ? formatCurrency(s.wage) : <span className="cell-na">—</span>}</td>
                        <td className="is-num">{commuteBaseOf(s) > 0 ? formatCurrency(commuteBaseOf(s)) : <span className="cell-na">—</span>}</td>
                        {columns.map((c) => trendCells(s.byCol.get(c.key), target, `${s.bb_id}-${c.key}`))}
                        {/* 最右の合計 */}
                        <td className="is-num col-group sheet-col-appt">{formatCount(s.agg.appointments)}</td>
                        <td className="is-num sheet-col-hours">{formatDecimal(s.agg.hours, { digits: 1 })}</td>
                        <td className="is-num sheet-col-pay">{formatCurrency(s.agg.pay)}</td>
                        <td className="is-num">{s.agg.commute > 0 ? formatCurrency(s.agg.commute) : <span className="cell-na">—</span>}</td>
                        <td className="is-num">{(advances[s.bb_id] ?? 0) > 0 ? formatCurrency(advances[s.bb_id]!) : <span className="cell-na">—</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="is-sticky" style={stickyNo} />
                    <th scope="row" className="is-sticky is-sticky-edge" style={stickyName}>
                      合計
                    </th>
                    <td colSpan={2} />
                    <td className="is-num">{formatCount(visible.reduce((a, x) => a + x.days, 0))}</td>
                    <td className="is-num">{formatDecimal(visibleTotal.hours, { digits: 1 })}</td>
                    <td colSpan={3} />
                    {columns.map((c) => (
                      <td key={`foot-${c.key}`} colSpan={4} className="col-group" />
                    ))}
                    <td className="is-num col-group sheet-col-appt">{formatCount(visibleTotal.appointments)}</td>
                    <td className="is-num sheet-col-hours">{formatDecimal(visibleTotal.hours, { digits: 1 })}</td>
                    <td className="is-num sheet-col-pay">{formatCurrency(visibleTotal.pay)}</td>
                    <td className="is-num">{formatCurrency(visibleTotal.commute)}</td>
                    <td className="is-num">{formatCurrency(visible.reduce((a, x) => a + (advances[x.bb_id] ?? 0), 0))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
